/**
 * The one fixed bottom slot (docs/design/MASTER.md section 5). The phone price bar on compound pages, the
 * "Contents" pill on database blog posts and the site-wide sticky CTA bar each ask for the slot with
 * useBottomSlot(); only the highest priority one that is asking draws (the order is in bottomSlotPriority.ts), so
 * two bars can never stack up or cover each other's buttons.
 *
 * The owner's height is kept free at the end of the page (body padding-bottom, through --bottom-slot-space in
 * index.css), so the footer's last lines can always scroll above the bar. The space is added at the end of the
 * document, so nothing on screen moves when a bar appears.
 *
 * Without a provider (a component rendered on its own in a test) the hook answers "yes" whenever the component
 * wants the slot, which is how the bars behaved before the slot existed.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { pickBottomSlotOwner, reservedSpace, type BottomSlotId } from "./bottomSlotPriority";

export { BOTTOM_SLOT_PRIORITY, pickBottomSlotOwner, type BottomSlotId } from "./bottomSlotPriority";
export { useMediaQuery } from "./useMediaQuery";

interface BottomSlotContextValue {
  owner: BottomSlotId | null;
  ask: (id: BottomSlotId, asking: boolean) => void;
  reserve: (id: BottomSlotId, px: number) => void;
}

const BottomSlotContext = createContext<BottomSlotContextValue | null>(null);

const SPACE_VAR = "--bottom-slot-space";

export function BottomSlotProvider({ children }: { children: ReactNode }) {
  const [asking, setAsking] = useState<ReadonlySet<BottomSlotId>>(() => new Set());
  const owner = useMemo(() => pickBottomSlotOwner(asking), [asking]);
  const spaces = useRef(new Map<BottomSlotId, number>());
  const ownerRef = useRef<BottomSlotId | null>(owner);

  const writeSpace = useCallback(() => {
    if (typeof document === "undefined") return;
    const id = ownerRef.current;
    const px = id ? spaces.current.get(id) ?? 0 : 0;
    const root = document.documentElement;
    if (px > 0) root.style.setProperty(SPACE_VAR, `${px}px`);
    else root.style.removeProperty(SPACE_VAR);
  }, []);

  useEffect(() => {
    ownerRef.current = owner;
    writeSpace();
  }, [owner, writeSpace]);

  useEffect(
    () => () => {
      document.documentElement.style.removeProperty(SPACE_VAR);
    },
    [],
  );

  const ask = useCallback((id: BottomSlotId, on: boolean) => {
    setAsking((prev) => {
      if (prev.has(id) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const reserve = useCallback(
    (id: BottomSlotId, px: number) => {
      if ((spaces.current.get(id) ?? 0) === px) return;
      if (px > 0) spaces.current.set(id, px);
      else spaces.current.delete(id);
      if (ownerRef.current === id) writeSpace();
    },
    [writeSpace],
  );

  const value = useMemo(() => ({ owner, ask, reserve }), [owner, ask, reserve]);
  return <BottomSlotContext.Provider value={value}>{children}</BottomSlotContext.Provider>;
}

/**
 * Ask for the bottom slot while `wants` is true. Draw the element only when `owns` is true, and put `slotRef` on
 * the fixed element so its height is kept free at the end of the page.
 */
export function useBottomSlot(id: BottomSlotId, wants: boolean): { owns: boolean; slotRef: (el: HTMLElement | null) => void } {
  const ctx = useContext(BottomSlotContext);
  const ask = ctx?.ask;
  const reserve = ctx?.reserve;

  useEffect(() => {
    if (!ask) return;
    ask(id, wants);
    return () => ask(id, false);
  }, [ask, id, wants]);

  const observer = useRef<ResizeObserver | null>(null);
  const slotRef = useCallback(
    (el: HTMLElement | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!reserve) return;
      if (!el) {
        reserve(id, 0);
        return;
      }
      // offsetHeight ignores transforms, so a bar still sliding in is measured at its resting size.
      const measure = () => reserve(id, reservedSpace(el.offsetHeight, parseFloat(window.getComputedStyle(el).bottom)));
      measure();
      if (typeof ResizeObserver !== "undefined") {
        observer.current = new ResizeObserver(measure);
        observer.current.observe(el);
      }
    },
    [id, reserve],
  );

  useEffect(() => () => observer.current?.disconnect(), []);

  return { owns: ctx ? wants && ctx.owner === id : wants, slotRef };
}
